import { useState, useEffect } from 'react';
import { useRefresh } from '../context/RefreshContext';

interface SocialLink {
  id: string;
  label: string;
  link: string;
  icon: string;
}

interface SystemSpec {
  id: string;
  label: string;
  value: string;
}

interface CS2Setting {
  id: string;
  label: string;
  value: string;
}

interface ViewModel {
  id: string;
  label: string;
  value: string;
}

interface VideoSetting {
  id: string;
  label: string;
  value: string;
}

interface Bind {
  id: string;
  label: string;
  key: string;
}

interface FAQ {
  id: string;
  question: string;
  answer: string;
}

interface ConfigSection {
  configLink: string;
  launchOptions: string;
  crosshair: string;
  viewmodel: string;
  hud: string;
  radar: string;
}

interface AdvancedVideoSetting {
  id: string;
  label: string;
  value: string;
  status?: 'enabled' | 'disabled';
}

interface Reward {
  id: string;
  title: string;
  imageUrl: string;
  link: string;
}

interface Sponsor {
  id: string;
  label: string;
  link: string;
}

interface ContentData {
  socialLinks: SocialLink[];
  systemSpecs: SystemSpec[];
  cs2Settings: CS2Setting[];
  viewmodel: ViewModel[];
  videoSettings: VideoSetting[];
  binds: Bind[];
  faqs: FAQ[];
  configSection: ConfigSection;
  mouseSettings: SystemSpec[];
  advancedVideoSettings: AdvancedVideoSetting[];
  rewards: Reward[];
  sponsors: Sponsor[];
}

const defaultContent: ContentData = {
  socialLinks: [
    { id: '1', label: 'Faceit', link: 'https://www.faceit.com/de/players/mika666', icon: 'faceit' },
    { id: '2', label: 'Steam', link: 'https://steamcommunity.com/profiles/76561198088755702', icon: 'steam' },
    { id: '3', label: 'Instagram', link: 'https://www.instagram.com/150kbtw/', icon: 'instagram' },
    { id: '4', label: 'Discord', link: 'https://discord.gg/CwX4TQxszq', icon: 'discord' },
    { id: '5', label: 'Twitter', link: 'https://x.com/150kbtw', icon: 'twitter' },
    { id: '6', label: 'Twitch', link: 'https://www.twitch.tv/150k_btw', icon: 'twitch' },
    { id: '7', label: 'Youtube', link: 'https://www.youtube.com/@150kbtw', icon: 'youtube' },
    { id: '8', label: 'Tiktok', link: 'https://www.tiktok.com/@150kbtw', icon: 'tiktok' },
  ],
  configSection: {
    configLink: 'https://drive.google.com/file/d/1wbpFIlQKe4vsAW0F36W1h4jG4oEZMoJ4/view?usp=sharing',
    launchOptions: 'Launch Options',
    crosshair: 'CSGO-Dadg-j6hfr-pH5eO-4onNB-VAmJ',
    viewmodel: 'viewmodel_fov 68;viewmodel_offset_x -2;viewmodel_offset_y 2;viewmodel_offset_z -2;cl_prefer_lefthanded true',
    hud: 'cl_hud_color 10;cl_showloadout true;safezone 1;safezoneY 1',
    radar: 'cl_hud_radar_scale 1.09962;cl_radar_scale 1;cl_radar_always_centered true;cl_radar_rotate true;cl_radar_icon_scale_min 0.6'
  },
  mouseSettings: [
    { id: '1', label: 'DPI', value: '800' },
    { id: '2', label: 'Mouse Sensitivity', value: '0.94' },
    { id: '3', label: 'Zoom Sensitivity Multiplier', value: '1.04' },
    { id: '4', label: 'Polling rate', value: '500 Hz' },
    { id: '5', label: 'm_yaw', value: '0.022' },
  ],
  advancedVideoSettings: [
    { id: '1', label: 'Boost Player Contrast', value: 'Enabled', status: 'enabled' },
    { id: '2', label: 'V-Sync', value: 'Disabled', status: 'disabled' },
    { id: '3', label: 'Multisampling Anti-Aliasing Mode', value: 'None' },
    { id: '4', label: 'Global Shadow Quality', value: 'Low' },
    { id: '5', label: 'Model / Texture Detail', value: 'Low' },
    { id: '6', label: 'Texture Filtering Mode', value: 'Bilinear' },
    { id: '7', label: 'Shader Detail', value: 'Low' },
    { id: '8', label: 'Particle Detail', value: 'Low' },
    { id: '9', label: 'Ambient Occlusion', value: 'Disabled' },
    { id: '10', label: 'High Dynamic Range', value: 'Performance' },
    { id: '11', label: 'FidelityFX Super Resolution', value: 'Disabled (Highest Quality)' },
    { id: '12', label: 'NVIDIA Reflex Low Latency', value: 'Disabled' },
  ],
  systemSpecs: [
    { id: '1', label: 'CPU', value: 'Amd Ryzen 9 5900x' },
    { id: '2', label: 'GPU', value: 'Asus Geforce TUF RTX 3080 O10G' },
    { id: '3', label: 'RAM', value: 'Dominator Platinum 32GB' },
    { id: '4', label: 'MOTHERBOARD', value: 'Asus TUF gaming b550+' },
    { id: '5', label: 'COOLER', value: 'Kraken X63' },
    { id: '6', label: 'SSD', value: 'Samsung 980 Pro 512GB' },
    { id: '7', label: 'MOUSE', value: 'Logitech G PRO X Superlight 2' },
    { id: '8', label: 'MOUSEPAD', value: 'Zowie Gear G-SR-SE' },
    { id: '9', label: 'MONITOR', value: 'BenQ Zowie 240 Hz' },
    { id: '10', label: 'KEYBOARD', value: 'Wooting 60HE+' },
    { id: '11', label: 'HEADPHONES', value: 'Steelseries Arctis Nova Pro Wireless' },
  ],
  cs2Settings: [
    { id: '1', label: 'DPI', value: '800' },
    { id: '2', label: 'Sensitivity', value: '0.94' },
    { id: '3', label: 'eDPI', value: '752' },
    { id: '4', label: 'Zoom Sensitivity', value: '1' },
    { id: '5', label: 'Windows Sensitivity', value: '6' },
    { id: '6', label: 'Hz', value: '4000' },
  ],
  viewmodel: [
    { id: '1', label: 'FOV', value: '68' },
    { id: '2', label: 'Offset X', value: '-2' },
    { id: '3', label: 'Offset Y', value: '2' },
    { id: '4', label: 'Offset Z', value: '-2' },
    { id: '5', label: 'Presets', value: '0' },
  ],
  videoSettings: [
    { id: '1', label: 'Resolution', value: '1280x1024' },
    { id: '2', label: 'Aspect Ratio', value: '4:3' },
    { id: '3', label: 'Scaling Mode', value: 'STRETCHED' },
    { id: '4', label: 'Brightness', value: '80%' },
    { id: '5', label: 'Display Mode', value: 'FULLSCREEN' },
  ],
  binds: [
    { id: '1', label: 'Toggle Inventory Display', key: 'I' },
    { id: '2', label: 'Move Forward', key: 'W' },
    { id: '3', label: 'Move Backward', key: 'S' },
    { id: '4', label: 'Move Left (strafe)', key: 'A' },
    { id: '5', label: 'Move Right (strafe)', key: 'D' },
    { id: '6', label: 'Walk', key: 'CAPSLOCK' },
    { id: '7', label: 'Duck', key: 'SHIFT' },
    { id: '8', label: 'Jump', key: 'SPACE' },
    { id: '9', label: 'Use', key: 'E' },
    { id: '10', label: 'Fire', key: 'MOUSE1' },
    { id: '11', label: 'Secondary Fire', key: 'MOUSE2' },
    { id: '12', label: 'Reload', key: 'R' },
    { id: '13', label: 'Radial Weapon Menu', key: 'nan' },
    { id: '14', label: 'Select Previous Weapon', key: 'MWHEELUP' },
    { id: '15', label: 'Select Next Weapon', key: 'MWHEELDOWN' },
    { id: '16', label: 'Last Weapon Used', key: 'Q' },
    { id: '17', label: 'Drop Weapon', key: 'G' },
    { id: '18', label: 'Inspect Weapon', key: 'F' },
    { id: '19', label: 'Switch Viewmodel Left/Right Hand', key: 'nan' },
    { id: '20', label: 'Buy Menu', key: 'B' },
    { id: '21', label: 'Autobuy', key: 'F3' },
    { id: '22', label: 'Rebuy', key: 'F4' },
    { id: '23', label: 'Primary Weapon', key: '1' },
    { id: '24', label: 'Secondary Weapon', key: '2' },
    { id: '25', label: 'Melee Weapons', key: '3' },
    { id: '26', label: 'Cycle Grenades', key: '4' },
    { id: '27', label: 'Explosives & Traps', key: '5' },
    { id: '28', label: 'HE Grenade', key: '6' },
    { id: '29', label: 'Flashbang', key: '7' },
    { id: '30', label: 'Smoke Grenade', key: '8' },
    { id: '31', label: 'Decoy Grenade', key: '9' },
    { id: '32', label: 'Molotov Cocktail / Incendiary Grenade', key: '0' },
    { id: '33', label: 'Zeus x27', key: 'X' },
    { id: '34', label: 'Medi-Shot', key: 'nan' },
    { id: '35', label: 'Utility Items', key: 'nan' },
    { id: '36', label: 'Graffiti Menu', key: 'T' },
    { id: '37', label: 'Scoreboard', key: 'TAB' },
    { id: '38', label: 'Show Team Equipment', key: 'nan' },
    { id: '39', label: 'Toggle Radar Zoom', key: 'V' },
    { id: '40', label: 'Call Vote', key: 'nan' },
    { id: '41', label: 'Choose Team', key: 'M' },
    { id: '42', label: 'Toggle Console', key: 'J' },
    { id: '43', label: 'Player Ping', key: 'nan' },
    { id: '44', label: 'Radio Message', key: 'nan' },
    { id: '45', label: 'Command Radio Message', key: 'nan' },
    { id: '46', label: 'Standard Radio Message', key: 'nan' },
    { id: '47', label: 'Report Radio Message', key: 'nan' },
    { id: '48', label: 'Team Message', key: 'U' },
    { id: '49', label: 'Chat Message', key: 'Z' },
    { id: '50', label: 'Use Microphone', key: 'MOUSE5' },
    { id: '51', label: 'Temporarily Disable Incoming Chat', key: 'nan' },
    { id: '52', label: 'Chat Wheel 1', key: 'nan' },
    { id: '53', label: 'Chat Wheel 2', key: 'nan' },
    { id: '54', label: 'Chat Wheel 3', key: 'nan' },
  ],
  faqs: [
    {
      id: '1',
      question: "How did you come up with the name 150k_btw?",
      answer: "Back in the day, when I was grinding Call of Duty, having a good internet connection gave you a real advantage. I had the best connection around—and because of that, people started calling me \"Connecto\". I wasn't a fan of the name, though, so I went with something else: \"150k\"—which was actually the speed of my connection back then. And that's how the name was born. EZ as that."
    },
    {
      id: '2',
      question: "Whats your name? Whats your Age? Whats your Height? Where are you from?",
      answer: "Mika 25 193cm Switzerland"
    },
    {
      id: '3',
      question: "How do you have VAC and Game Bans",
      answer: "MW3 Modded Lobbies / COD Ghost Levelhack (10 Years ago)"
    },
    {
      id: '4',
      question: "How do I join the community?",
      answer: "Click the Discord or Steam icon on Homepage to join our community and get started!"
    },
    {
      id: '5',
      question: "Where can I find your config?",
      answer: "The config is available on the settings page. Click the Config button to download."
    }
  ],
  rewards: [
    {
      id: '1',
      title: 'Ongoing Giveaway',
      imageUrl: '/giveaway-image.jpg',
      link: '#'
    }
  ],
  sponsors: [
    {
      id: '1',
      label: 'Sponsor 1',
      link: 'https://example.com'
    }
  ]
};

export const useContent = () => {
  const { refresh } = useRefresh();
  const [content, setContent] = useState<ContentData>(defaultContent);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchContent = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch('/api/content');
      if (!response.ok) {
        throw new Error('Failed to fetch content');
      }
      
      const data = await response.json();
      setContent(data);
    } catch (err) {
      console.error('Error fetching content:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch content');
      // Fallback to default content
      setContent(defaultContent);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContent();
  }, [refresh]);

  return {
    content,
    loading,
    error,
    refetch: fetchContent
  };
};

export type { SocialLink, SystemSpec, CS2Setting, ViewModel, VideoSetting, Bind, FAQ, ContentData, ConfigSection, AdvancedVideoSetting, Reward, Sponsor }; 